/**
 * Binary Encoding Constants for Universal Create Menu (V2 API)
 *
 * This module defines the constants used for encoding/decoding ATI configurations
 * into a compact binary format for URL transmission.
 *
 * These constants are shared across frontend and backend for binary encoding/decoding operations.
 */

import { createIndexMap } from './ariUtils';

// =============================================================================
// Schema Version
// =============================================================================

/**
 * Increment when making breaking changes to the binary format
 * Version 2: Hybrid delimiter-based format with binary ATI encoding
 */
export const SCHEMA_VERSION = 2;

// =============================================================================
// ATI Prefix Mapping
// =============================================================================

/**
 * Known ATI prefixes that identify the deployment environment or platform.
 * Source of truth for all ATI prefix encodings.
 *
 * With the new bit layout:
 * - 'cloud' maps to prefix_value=0 (when prefix_flag=0)
 * - 'third-party' maps to prefix_value=1 (when prefix_flag=0)
 * - Unknown prefixes use prefix_flag=1 with prefix_value as code (0-1) for up to 2 unknown prefixes
 */
export const KNOWN_ATI_PREFIXES = ['cloud', 'third-party'] as const;

/**
 * Known ATI prefix mappings (1 bit prefix_value = 2 possible known values)
 * Computed from KNOWN_ATI_PREFIXES array.
 * When prefix_flag=1, prefix_value bit is used as unknown prefix code (0-1).
 */
export const ATI_PREFIX_MAP: Record<string, number> = createIndexMap(KNOWN_ATI_PREFIXES);

// =============================================================================
// Resource Owner Mapping
// =============================================================================

/**
 * Known resource owners representing Atlassian products that can own resources.
 * Source of truth for all resource owner encodings.
 *
 * These identifiers map to specific Atlassian products that create and manage
 * resources within the Universal Create ecosystem.
 *
 * Based on Atlassian ARI Registry:
 * https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry/
 *
 * Order matters: indices are used for binary encoding (8-bit = 256 max owners).
 *
 * All new items must be added at the end
 * !!! Important: Do not change the order of existing entries !!!
 * !!! Important: Do not change the order of existing entries !!!
 * !!! Important: Do not change the order of existing entries !!!
 */
export const KNOWN_ATI_RESOURCE_OWNERS = [
	// ALL NEW RESOURCE OWNERS MUST BE ADDED AT THE END OF THIS LIST
	'jira',
	'confluence',
	'loom',
	'townsquare',
	'trello',
	'bitbucket',
	'opsgenie',
	'compass',
	'statuspage',
	'atlas',
	// ALL NEW RESOURCE OWNERS MUST BE ADDED AT THE END OF THIS LIST
] as const;

/**
 * Known resource owner mappings (8 bits = 256 possible values)
 * Computed from KNOWN_ATI_RESOURCE_OWNERS array.
 */
export const RESOURCE_OWNER_MAP: Record<string, number> = createIndexMap(KNOWN_ATI_RESOURCE_OWNERS);

// =============================================================================
// Resource Type Mapping
// =============================================================================

/**
 * Known resource types that can be created across Atlassian products.
 * Source of truth for all resource type encodings.
 *
 * These identifiers represent the specific types of resources that can be created
 * through Universal Create and other cross-product features.
 *
 * Based on Atlassian ARI Registry:
 * https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry/
 *
 * Order matters: indices are used for binary encoding (12-bit = 4096 max types).
 * Only critical resource types are included.
 *
 * All new items must be added at the end
 * !!! Important: Do not change the order of existing entries !!!
 * !!! Important: Do not change the order of existing entries !!!
 * !!! Important: Do not change the order of existing entries !!!
 */
export const KNOWN_ATI_RESOURCE_TYPES = [
	// ALL NEW RESOURCE TYPES MUST BE ADDED AT THE END OF THIS LIST
	'issue',
	'page',
	'blogpost',
	'whiteboard',
	'database',
	'space',
	'video',
	'goal',
	'project',
	'team',
	'post',
	'board',
	'repository',
	'workspace',
	'component',
	'embed',
	'livedoc',
	'chat',
	'agent',
	// ALL NEW RESOURCE TYPES MUST BE ADDED AT THE END OF THIS LIST
] as const;

/**
 * Known resource type mappings (12 bits = 4096 possible values)
 * Computed from KNOWN_ATI_RESOURCE_TYPES array.
 */
export const RESOURCE_TYPE_MAP: Record<string, number> = createIndexMap(KNOWN_ATI_RESOURCE_TYPES);

// =============================================================================
// Binary Format Constants
// =============================================================================

/**
 * Number of bytes used to encode each ATI in the binary format
 */
export const BYTES_PER_ATI = 3;

/**
 * Size of the binary header (version + section count)
 */
export const HEADER_SIZE = 1;

/**
 * Bit masks and shifts for encoding/decoding ATI components
 * Note: Using Big-Endian bit numbering (bit 7 = MSB, bit 0 = LSB)
 *
 * Byte 0: [P|O|T|P|OOOO]
 *   P = Prefix flag (bit 7): 0=known, 1=unknown
 *   O = Owner flag (bit 6): 0=known, 1=unknown
 *   T = Type flag (bit 5): 0=known, 1=unknown
 *   P = Prefix value (bit 4): 0=cloud, 1=third-party (or prefix code if unknown)
 *   OOOO = Owner upper 4 bits (bits 3-0)
 */
export const BIT_MASKS = {
	// Byte 0 masks
	PREFIX_FLAG_MASK: 0b10000000, // Bit 7
	OWNER_FLAG_MASK: 0b01000000, // Bit 6
	TYPE_FLAG_MASK: 0b00100000, // Bit 5
	PREFIX_VALUE_MASK: 0b00010000, // Bit 4
	OWNER_UPPER_MASK: 0b00001111, // Bits 3-0 (bottom 4 bits)

	// Byte 1 masks
	OWNER_LOWER_MASK: 0b11110000, // Bits 7-4 (top 4 bits)
	TYPE_UPPER_MASK: 0b00001111, // Bits 3-0 (bottom 4 bits)

	// Byte 2 masks
	TYPE_LOWER_MASK: 0b11111111, // All 8 bits
} as const;

/**
 * Bit shift amounts for encoding/decoding
 */
export const BIT_SHIFTS = {
	PREFIX_FLAG_SHIFT: 7,
	OWNER_FLAG_SHIFT: 6,
	TYPE_FLAG_SHIFT: 5,
	PREFIX_VALUE_SHIFT: 4,
	OWNER_LOWER_SHIFT: 4,
	OWNER_UPPER_BITS: 4,
	TYPE_UPPER_SHIFT: 4,
	TYPE_UPPER_BITS: 8,
} as const;

// =============================================================================
// Size Limits
// =============================================================================

/**
 * Maximum number of known owners (8-bit code)
 */
export const MAX_KNOWN_OWNERS = 256;

/**
 * Maximum number of known types (12-bit code)
 */
export const MAX_KNOWN_TYPES = 4096;

/**
 * Maximum number of unknown owners that can be passed in query params
 */
export const MAX_UNKNOWN_OWNERS = 256;

/**
 * Maximum number of unknown types that can be passed in query params
 */
export const MAX_UNKNOWN_TYPES = 4096;

/**
 * Maximum number of unknown prefixes that can be passed in query params
 * Codes 2-3 map to indices 0-1, so max 2 unknown prefixes
 */
export const MAX_UNKNOWN_PREFIXES = 2;

/**
 * Maximum number of sections allowed in a configuration
 * Reduced from theoretical max (15) to practical limit
 */
export const MAX_SECTIONS = 5;

/**
 * Maximum number of items per section (8-bit count)
 */
export const MAX_ITEMS_PER_SECTION = 255;

/**
 * Maximum length of base64url-encoded config string to prevent DoS attacks
 * Conservative limit based on expected usage:
 * - 64 ATIs @ 3 bytes each = 192 bytes raw
 * - Base64url encoding: 192 bytes -> ~256 chars
 * - Additional metadata and delimiters
 * - 512 char limit provides comfortable headroom
 * Note: Decoded output is always shorter (3 bytes from 4 chars), so this check
 * is sufficient to prevent memory exhaustion
 */
export const MAX_ENCODED_LENGTH = 512;

/**
 * Maximum number of parts in a section
 * Allows for future expansion while preventing abuse
 */
export const MAX_SECTION_PARTS = 10;

// =============================================================================
// Menu Item Type Mapping
// =============================================================================

/**
 * Menu item types for appDefinedSection items.
 * Source of truth for item type encodings.
 *
 * These represent the different types of items that can appear in a menu section:
 * - native: Items belonging to the current application
 * - link: Direct links to cross-application resources
 * - platform: Platform-level components or actions
 */
export const MENU_ITEM_TYPES = ['native', 'link', 'platform'] as const;

/**
 * Menu item type mappings
 * Computed from MENU_ITEM_TYPES array.
 */
export const MENU_ITEM_TYPE_MAP: Record<string, number> = createIndexMap(MENU_ITEM_TYPES);

// =============================================================================
// Personalize Mode Mapping
// =============================================================================

/**
 * Personalize modes for app-defined sections.
 * Source of truth for personalize mode encodings.
 *
 * These represent the different personalization strategies:
 * - off: No personalization applied (index 0)
 * - full: Full personalization with all items personalized (index 1)
 * - overflow: Only overflow items are personalized (index 2)
 */
export const PERSONALIZE_MODES = ['off', 'full', 'overflow'] as const;

/**
 * Personalize mode mappings
 * Computed from PERSONALIZE_MODES array.
 */
export const PERSONALIZE_MODE_MAP: Record<string, number> = createIndexMap(PERSONALIZE_MODES);

// =============================================================================
// Menu Section Types
// =============================================================================

/**
 * Section types in the order they are encoded.
 * Index in this array maps to the numeric code used in encoding.
 */
export const SECTION_TYPES = ['appDefinedSection', 'suggestionSection'] as const;

/**
 * Section type mappings
 * Computed from SECTION_TYPES array.
 */
export const SECTION_TYPE_MAP: Record<string, number> = createIndexMap(SECTION_TYPES);

// =============================================================================
// Hierarchical Delimiters
// =============================================================================

/**
 * Hierarchical delimiters used in the binary encoding format.
 * Higher-level delimiters contain structures with lower-level delimiters.
 *
 * Level 0 (highest): '~' - Separates sections
 *   Example: section1~section2~section3
 *
 * Level 1: '|' - Separates fields within a section
 *   Example: 0|items|personalize or 1|limit|excludeAtis|preferredAtis
 *
 * Level 2: ',' - Separates items in a list
 *   Example: item1,item2,item3 or ati1,ati2,ati3
 *
 * Level 3 (lowest): ':' - Separates properties within an item
 *   Example: atiBase64:typeCode:promoted
 */
export const DELIMITERS = ['~', '|', ',', ':'] as const;
